import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import flagsmith from 'flagsmith';
import { FlagsmithProvider } from 'flagsmith/react';
import { FlagsmithContextType } from 'flagsmith/src/react';
import { Store } from '@reduxjs/toolkit';

import { createHttpApiService, HttpApiServiceContext } from 'app/api';
import { FetchContext } from 'app/api/useFetch/context';
import { App } from 'app/App';
import { LanguageStore } from 'app/lang';
import ConnectedRouter from 'app/lib/router';
import { Theme, ThemeContextProvider } from 'app/lib/theme';
import { UserAgentProvider } from 'app/lib/user-agent';
import Routes from 'app/routes';
import ErrorBoundary from 'app/widgets/ErrorBoundary';

type ClientWithProfilerProps = {
  store: Store;
  flagsmithState: FlagsmithContextType['serverState'];
  initialTheme: Theme;
};

export const AppWrapperForBrowser = ({
  store, flagsmithState, initialTheme,
}: ClientWithProfilerProps) => {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <I18nextProvider i18n={LanguageStore.getInstance()}>
          <Provider store={store}>
            <ConnectedRouter>
              <FetchContext.Provider value={window.__useFetchState}>
                <FlagsmithProvider serverState={flagsmithState}
                                   options={{
                                     cacheFlags: true,
                                     environmentID: flagsmithState?.environmentID || '',
                                   }}
                                   flagsmith={flagsmith} >
                  <ThemeContextProvider initialTheme={initialTheme}>
                    <UserAgentProvider value={navigator.userAgent}>
                      <HttpApiServiceContext.Provider value={createHttpApiService}>
                        <App query={undefined}>
                          <Routes />
                        </App>
                      </HttpApiServiceContext.Provider>
                    </UserAgentProvider>
                  </ThemeContextProvider>
                </FlagsmithProvider>
              </FetchContext.Provider>
            </ConnectedRouter>
          </Provider>
        </I18nextProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};
